import React, { Suspense, lazy, useEffect } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TrainingProvider } from "./context";
import { PageSkeleton, Pill } from "./components/ui";
import AgentHome from "./agent/AgentHome";
import { AUTHORING_ENABLED } from "./constants";
import "./training.css";

// heavier / rarely-first screens are split out of the main bundle
const ScenarioPage = lazy(() => import("./agent/ScenarioPage"));
const LiveCall = lazy(() => import("./live/LiveCall"));
const Debrief = lazy(() => import("./agent/Debrief"));
const TeamOverview = lazy(() => import("./manager/TeamOverview"));
const AgentDetail = lazy(() => import("./manager/AgentDetail"));
const Programs = lazy(() => import("./manager/Programs"));
const ProgramEditor = lazy(() => import("./manager/ProgramEditor"));
const ScenarioEditor = lazy(() => import("./manager/ScenarioEditor"));
const Generate = lazy(() => import("./manager/Generate"));
const AgentLab = lazy(() => import("./live/AgentLab"));
const NavTab = ({ to, end, children }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `trn-tab ${isActive ? "is-active" : ""}`}
    aria-current={undefined}
  >
    {children}
  </NavLink>
);
const ModuleNav = ({ env }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const b = env.basePath;
  // switching view also moves to that view's home: the current URL belongs to the other role
  const switchTo = (r) => {
    env.setRole(r);
    navigate(r === "manager" ? `${b}/team` : b);
  };
  return (
    <div className="trn-modnav">
      <span className="trn-modnav__title">{t("Formation")}</span>
      <nav
        className="trn-modnav__tabs"
        aria-label={t("Formation")}
      >
        {env.roles.agent && env.role === "agent" && (
          <NavTab
            to={b}
            end
          >
            {t("Mon parcours")}
          </NavTab>
        )}
        {env.roles.manager && env.role === "manager" && (
          <>
            <NavTab to={`${b}/team`}>{t("Équipe")}</NavTab>
            <NavTab to={`${b}/programs`}>{t("Parcours")}</NavTab>
            {AUTHORING_ENABLED && <NavTab to={`${b}/generate`}>{t("Créer avec l'IA")}</NavTab>}
          </>
        )}
        {env.dev && <NavTab to={`${b}/lab`}>{t("Agent Lab")}</NavTab>}
      </nav>
      {env.roles.agent && env.roles.manager && (
        <div
          className="trn-seg"
          role="group"
          aria-label={t("Vue")}
        >
          {["agent", "manager"].map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={env.role === r}
              onClick={() => switchTo(r)}
            >
              {r === "agent" ? t("Vue agent") : t("Vue manager")}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
const Shell = ({ env }) => {
  const { pathname } = useLocation();
  // the live call is a full-screen experience: no module nav around it
  const immersive = /\/live\//.test(pathname);
  // deep links decide the role: a manager URL shows the manager view, and vice versa
  const managerPath = /\/(team|programs|generate)(\/|$)/.test(pathname);
  const agentPath = !managerPath && /\/(scenario|live|debrief)\//.test(pathname);
  useEffect(() => {
    if (managerPath && env.roles.manager && env.role !== "manager") env.setRole("manager");
    if (agentPath && env.roles.agent && env.role !== "agent") env.setRole("agent");
    // eslint-disable-next-line
  }, [managerPath, agentPath]);
  const home = env.role === "manager" ? "team" : "";
  // the manager screens are for ADMIN_TRAINING only: anyone else lands on their own path
  if (managerPath && !env.roles.manager) {
    return (
      <Navigate
        to={env.basePath}
        replace
      />
    );
  }
  return (
    <div className="trn trn-root">
      {!immersive && <ModuleNav env={env} />}
      <div className={immersive ? "" : "trn-page"}>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route
              index
              element={
                env.role === "manager" ? (
                  <Navigate
                    to={`${env.basePath}/team`}
                    replace
                  />
                ) : (
                  <AgentHome />
                )
              }
            />
            <Route
              path="scenario/:scenarioId"
              element={<ScenarioPage />}
            />
            <Route
              path="live/:scenarioId"
              element={<LiveCall />}
            />
            <Route
              path="debrief/:attemptId"
              element={<Debrief />}
            />
            <Route
              path="team"
              element={<TeamOverview />}
            />
            <Route
              path="team/:operatorId"
              element={<AgentDetail />}
            />
            <Route
              path="programs"
              element={<Programs />}
            />
            <Route
              path="programs/:programId"
              element={<ProgramEditor />}
            />
            <Route
              path="programs/:programId/scenario/:scenarioId"
              element={<ScenarioEditor />}
            />
            <Route
              path="generate"
              element={
                AUTHORING_ENABLED ? (
                  <Generate />
                ) : (
                  <Navigate
                    to={`${env.basePath}/programs`}
                    replace
                  />
                )
              }
            />
            <Route
              path="lab"
              element={<AgentLab />}
            />
            <Route
              path="*"
              element={
                <Navigate
                  to={`${env.basePath}/${home}`}
                  replace
                />
              }
            />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};
const TrainingApp = ({ api, env }) => (
  <TrainingProvider
    api={api}
    env={env}
  >
    <Shell env={env} />
  </TrainingProvider>
);
export default TrainingApp;
